// Frontend-only NMS auth gate helpers.
//
// The NMS dashboard's @argon AuthProvider persists its token set in
// localStorage under "rawTokenSet" and the pre-login location under
// "intendedUrl" (both JSON-stringified). LOPA is served on the same origin
// (behind the dashboard nginx at /lopa/), so it can read the token set
// directly and hand unauthenticated users back to the dashboard login.
//
// This is a UX gate, not API security — the LOPA API itself stays
// VPN-protected, same as with AUTH_REQUIRED=false.

const TOKEN_SET_KEY = "rawTokenSet";
const INTENDED_URL_KEY = "intendedUrl";
const EXP_SKEW_SECONDS = 30;

interface RawTokenSet {
  accessToken?: string;
}

function readRawTokenSet(): RawTokenSet | null {
  try {
    const raw = localStorage.getItem(TOKEN_SET_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as RawTokenSet;
  } catch {
    return null;
  }
}

function decodeJwtExp(token: string): number | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: unknown };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

// Present, well-formed, and not (about to be) expired.
export function nmsTokenValid(): boolean {
  const accessToken = readRawTokenSet()?.accessToken;
  if (!accessToken) return false;
  const exp = decodeJwtExp(accessToken);
  if (exp === null) return false;
  return exp - EXP_SKEW_SECONDS > Date.now() / 1000;
}

// Hand over to the NMS dashboard login (site root on the shared origin),
// leaving the current LOPA deep link where the dashboard's AuthProvider
// looks for its post-login redirect.
export function redirectToNmsLogin(): void {
  try {
    localStorage.setItem(
      INTENDED_URL_KEY,
      JSON.stringify(`${window.location.pathname}${window.location.search}`),
    );
  } catch {
    // Best effort — losing the deep link only costs the user a click.
  }
  window.location.href = "/";
}
